import { useEffect, useState, useRef } from "react";
import { Crisp } from "crisp-sdk-web";
import { useProductContext } from "@/contexts/ProductContext";
import { getCurrentUser } from "@/lib/api/services/auth";
import { getProfile } from "@/lib/api/services/profile";

export const CrispChat = () => {
  const { productConfig, currentProduct } = useProductContext();
  const [shouldLoad, setShouldLoad] = useState(false);
  const currentCrispIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Defer Crisp loading until page is interactive
    // Load after 3 seconds OR on user interaction (whichever comes first)
    const timer = setTimeout(() => {
      setShouldLoad(true);
    }, 3000);

    const handleInteraction = () => {
      setShouldLoad(true);
      cleanup();
    };

    const cleanup = () => {
      clearTimeout(timer);
      document.removeEventListener('mousemove', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };

    // Listen for user interactions
    document.addEventListener('mousemove', handleInteraction, { once: true });
    document.addEventListener('scroll', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });

    return cleanup;
  }, []);

  // Load user context and set in Crisp
  const loadUserContext = async () => {
    try {
      const userResult = await getCurrentUser();
      if (userResult.success && userResult.data) {
        const user = userResult.data;

        if (user.email) {
          Crisp.user.setEmail(user.email);
        }

        const profileResult = await getProfile(user.id);
        if (profileResult.success && profileResult.data?.full_name) {
          Crisp.user.setNickname(profileResult.data.full_name);
        }
      }
    } catch (error) {
      console.warn("Could not load user context for Crisp:", error);
    }
  };

  // Initialize Crisp with product-specific configuration
  const initializeCrisp = async (crispWebsiteId: string) => {
    try {
      console.log(`Initializing Crisp for ${productConfig.name} (ID: ${crispWebsiteId})`);

      // Configure Crisp with product-specific Website ID
      Crisp.configure(crispWebsiteId, {
        safeMode: true,
        autoload: true,
        sessionMerge: true,
      });

      currentCrispIdRef.current = crispWebsiteId;

      // Set product segments for agent visibility
      Crisp.session.setSegments([
        `${currentProduct}_user`,
        productConfig.slug,
      ], true);

      // Set session data with product info
      Crisp.session.setData({
        product_name: productConfig.name,
        product_id: currentProduct,
        product_tagline: productConfig.branding.tagline,
      });

      // Load user context
      await loadUserContext();

      // On mobile: hide the floating bubble (users use Chat tab instead)
      // On desktop: show the floating widget
      const isMobile = window.innerWidth < 1024;
      if (isMobile) {
        Crisp.chat.hide();
        console.log(`Crisp bubble hidden on mobile - users will use Chat tab`);
      } else {
        Crisp.chat.show();
        console.log(`Crisp chat initialized for ${productConfig.name}`);
      }
    } catch (error) {
      console.error('Error initializing Crisp:', error);
    }
  };

  useEffect(() => {
    if (!shouldLoad) return;

    const crispWebsiteId = productConfig.branding.crispWebsiteId;

    // Check localStorage for previously initialized workspace
    const lastWorkspaceId = localStorage.getItem('crisp_workspace_id');

    // Check if we're switching to a different product's workspace
    const isProductSwitch = (lastWorkspaceId && lastWorkspaceId !== crispWebsiteId) ||
                            (currentCrispIdRef.current && currentCrispIdRef.current !== crispWebsiteId);

    if (isProductSwitch) {
      console.log(`Product switch detected: ${lastWorkspaceId || currentCrispIdRef.current} → ${crispWebsiteId}`);
      console.log('Forcing page reload to reinitialize Crisp with new workspace...');

      localStorage.setItem('crisp_workspace_id', crispWebsiteId);
      window.location.reload();
      return;
    }

    // First-time initialization (no product switch)
    if (!currentCrispIdRef.current) {
      initializeCrisp(crispWebsiteId);
      localStorage.setItem('crisp_workspace_id', crispWebsiteId);
    }

    // Handle resize: show/hide bubble based on screen size
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024;
      if (isMobile) {
        Crisp.chat.hide();
      } else {
        Crisp.chat.show();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (currentCrispIdRef.current) {
        Crisp.chat.hide();
      }
    };
  }, [shouldLoad, productConfig.branding.crispWebsiteId, currentProduct, productConfig.name, productConfig.slug, productConfig.branding.tagline]);

  return null;
};